/**
 * PhaseBoardColumn Component
 * Phase column for the board view (no drag-drop)
 */

import { Paper, Group, Text, Badge, Progress, Stack, Button, ScrollArea } from '@mantine/core';
import { Plus } from 'react-feather';
import type { TaskWithDetails, TaskStatus, TaskPhase, TaskPhaseRecord } from '../../../../types';
import {
  calculatePhaseStatus,
  getPhaseStatusConfig,
  PHASE_DISPLAY_NAMES,
} from '../../../../config/taskConfig';
import { BoardTaskCard } from './BoardTaskCard';

interface PhaseBoardColumnProps {
  /** Phase record from task_phases table, or legacy phase enum value */
  phase: TaskPhaseRecord | TaskPhase;
  /** Tasks belonging to this phase (filtered for display) */
  tasks: TaskWithDetails[];
  /** All tasks for this phase (unfiltered — for status badge and progress calculations) */
  allTasks?: TaskWithDetails[];
  /** Whether a filter or search is currently active */
  isFilterActive?: boolean;
  /** Callback when task completion is toggled */
  onToggleComplete: (taskId: string, currentStatus: TaskStatus) => void;
  /** Callback when task is clicked */
  onTaskClick: (task: TaskWithDetails) => void;
  /** Callback when add task is clicked */
  onAddTask?: (phase: TaskPhaseRecord | TaskPhase) => void;
  /** MED-770: Cohort ID for generating copy-link URLs */
  cohortId?: string;
}

export function PhaseBoardColumn({
  phase,
  tasks,
  allTasks,
  isFilterActive = false,
  onToggleComplete,
  onTaskClick,
  onAddTask,
  cohortId,
}: PhaseBoardColumnProps) {
  // Handle both TaskPhaseRecord and legacy TaskPhase enum
  const phaseName = typeof phase === 'string'
    ? PHASE_DISPLAY_NAMES[phase] || phase
    : phase.name;

  // Use unfiltered tasks for status badge and progress (always reflect full dataset)
  const statusTasks = allTasks ?? tasks;
  const completedCount = statusTasks.filter((t) => t.status === 'complete').length;
  const totalCount = statusTasks.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const filteredCount = tasks.length;

  // Calculate phase status from UNFILTERED data
  const phaseStatus = calculatePhaseStatus(statusTasks);
  const statusConfig = getPhaseStatusConfig(phaseStatus);

  return (
    <Paper
      withBorder
      radius="md"
      p={0}
      style={{
        width: 320,
        minWidth: 320,
        maxHeight: 'calc(100vh - 280px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      {/* Column Header */}
      <Stack gap="xs" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
        <Group justify="space-between">
          <Text fw={600} size="sm">
            {phaseName}
          </Text>
          <Badge
            size="xs"
            color={statusConfig.color}
            variant={statusConfig.variant}
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
            }}
          >
            {statusConfig.label}
          </Badge>
        </Group>

        {/* Progress Bar and Count */}
        <Group gap="sm">
          <Progress
            value={progressPercent}
            size="xs"
            color="medleyGreen"
            style={{ flex: 1 }}
          />
          <Text size="xs" c="dimmed" fw={500}>
            {isFilterActive
              ? `${filteredCount} result${filteredCount !== 1 ? 's' : ''}`
              : `${completedCount}/${totalCount}`}
          </Text>
        </Group>
      </Stack>

      {/* Task Cards */}
      <ScrollArea style={{ flex: 1 }} p="sm" type="auto">
        {tasks.length > 0 ? (
          tasks.map((task) => (
            <BoardTaskCard
              key={task.id}
              task={task}
              onToggleComplete={onToggleComplete}
              onClick={onTaskClick}
              cohortId={cohortId}
            />
          ))
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            No tasks
          </Text>
        )}
      </ScrollArea>

      {/* Add Task Button */}
      {onAddTask && (
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          leftSection={<Plus size={14} />}
          onClick={() => onAddTask(phase)}
          style={{ margin: 'var(--mantine-spacing-sm)' }}
          fullWidth
        >
          Add Task
        </Button>
      )}
    </Paper>
  );
}

export default PhaseBoardColumn;
